import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { PataviumRole } from '../models/request-and-responses/rest-api/enums/role.enum.model';
import { DataService } from './data.service';
import { IView } from '../models/request-and-responses/rest-api/interfaces/view';
import { SieveOperator, SieveString } from '../helpers/sieve/sieveString.class';
import { IUserWithAnagraphic } from '../models/request-and-responses/rest-api/interfaces/userWithAnagraphic';
import { IUserInfoLogin } from '../models/request-and-responses/rest-api/interfaces/userInfoLogin';
import { Observable } from 'rxjs/Observable';
import {
    IPataviumPagedResponse,
    IPataviumResponseFirstItem,
    PataviumPagedResponseFirstItem
} from '../helpers/response.class';
import { SieveFilter } from '../helpers/sieve/sieve-filters-and-sorts';
import { catchError, map, tap } from 'rxjs/operators';
import { of, Subscription, throwError } from 'rxjs';
import { SessionStorageService } from './session-storage.service';
import { WebSocketSessionService } from './web-socket/web-socket-session.service';

@Injectable()
export class AuthService {
    private readonly logoutWsEventSubscription: Subscription;

    constructor(private router: Router,
                private http: HttpClient,
                private webSocketMessageService: WebSocketSessionService) {

        this.logoutWsEventSubscription = this.webSocketMessageService.EVENTS.LOGOUT
            .observable
            .pipe(tap(async e => {
                console.debug(`Triggered ${e.description} event`);
                await this.logout();
            }))
            .subscribe();
    }

    public static getScreenSize(): string {
        const height = window.screen.height;
        const width = window.screen.width;
        return `${width}x${height}`;
    }

    login(
        body: IUserInfoLogin
    ): Observable<HttpResponse<IPataviumResponseFirstItem<IUserWithAnagraphic>>> {
        return this.http
            .post<IPataviumPagedResponse<IUserWithAnagraphic>>(
                environment.apiUrl + '/users/identity',
                body,
                { observe: 'response' }
            )
            .pipe(
                map(response =>
                    response.clone({ body: new PataviumPagedResponseFirstItem(response.body) })
                ),
                catchError(err => {
                    return throwError(err);
                })
            );
    }

    resetPassword(
        userId: string,
        passwordResetToken: string,
        newPassword: string
    ): Observable<IPataviumResponseFirstItem<IUserWithAnagraphic>> {
        return this.http
            .post<IPataviumPagedResponse<IUserWithAnagraphic>>(
                `${environment.apiUrl}/users/${userId}/password-reset/${passwordResetToken}`,
                newPassword,
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            )
            .pipe(map(response => new PataviumPagedResponseFirstItem(response)));
    }

    askForPasswordReset(userId: string) {
        return this.http
            .post<IPataviumPagedResponse<IUserWithAnagraphic>>(
                `${environment.apiUrl}/users/${userId}/password-reset/`,
                null,
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            )
            .pipe(map(response => new PataviumPagedResponseFirstItem(response)));
    }

    identifyUserByResetPasswordToken(
        userId: string,
        passwordResetToken: string
    ): Observable<IPataviumResponseFirstItem<IUserWithAnagraphic>> {
        return this.http
            .get<IPataviumPagedResponse<IUserWithAnagraphic>>(
                `${environment.apiUrl}/users/${userId}/password-reset/${passwordResetToken}`
            )
            .pipe(map(response => new PataviumPagedResponseFirstItem(response)));
    }

    async logout(): Promise<void> {
        SessionStorageService.clear();
        await this.router.navigate(['/']);
    }
}

/**AUTH GUARD BASE */
class AuthGuardBase {
    dataService: DataService;
    router: Router;
    constructor(dataService: DataService, router: Router) {
        this.dataService = dataService;
        this.router = router;
    }

    getViewPermissions(sieveString: SieveString) {
        return this.dataService.getViewPermissions(sieveString);
    }
    canBeVisited(sidebarName: string, response: IPataviumPagedResponse<IView>) {
        for (const view of response.payload) {
            if (view.name === sidebarName) {
                return view.canBeVisited;
            }
        }
        return false;
    }
    firstActiveSidebarName(response: IPataviumPagedResponse<IView>) {
        for (const view of response.payload) {
            if (view.canBeVisited === true) {
                return view.name;
            }
        }
    }
    firstActiveStateUrl(response: IPataviumPagedResponse<IView>, mapStateUrlSidebarName: object) {
        const activeSidebarName = this.firstActiveSidebarName(response);
        const activeStateUrl = this.getStateUrlFromSidebarName(
            activeSidebarName,
            mapStateUrlSidebarName
        );
        return activeStateUrl;
    }
    getSidebarNameFromStateUrl(stateUrl: string, mapStateUrlSidebarName: object) {
        for (const key in mapStateUrlSidebarName) {
            if (key === stateUrl) {
                return mapStateUrlSidebarName[key];
            }
        }
        return null;
    }
    getStateUrlFromSidebarName(sidebarName: string, mapStateUrlSidebarName: object) {
        for (const key in mapStateUrlSidebarName) {
            if (mapStateUrlSidebarName[key] === sidebarName) {
                return key;
            }
        }
        return null;
    }

    acccessThisRouteOrRedirect(
        role: PataviumRole,
        stateUrl: string,
        urlPref: string,
        mapStateUrlSidebarName: object
    ): Observable<boolean> {
        const sieveString = new SieveString({
            filters: [{ key: SieveFilter.View.Role, operator: SieveOperator.Equal, value: role }]
        });
        const views = this.getViewPermissions(sieveString);
        const sidebarName = this.getSidebarNameFromStateUrl(stateUrl, mapStateUrlSidebarName);
        if (SessionStorageService.jwt.get() === null) {
            console.error(`JWT is null: redirect to login page`);
            this.router.navigate(['']);
            return of(false);
        }

        return views.pipe(
            map(response => {
                const canBeVisited = this.canBeVisited(sidebarName, response);

                if (!canBeVisited) {
                    //redirect
                    let newStateUrl = this.firstActiveStateUrl(response, mapStateUrlSidebarName);

                    if (newStateUrl == null) {
                        return false;
                    }
                    newStateUrl = urlPref + newStateUrl;
                    this.router.navigate([newStateUrl]);
                }

                return canBeVisited;
            })
        );
    }
}

/** AUTH GUARD OPERATOR */
@Injectable({ providedIn: 'root' })
export class AuthGuardOperator extends AuthGuardBase implements CanActivateChild {
    _role: PataviumRole = PataviumRole.BackOfficeOperator;
    _urlPref = '/admin/';
    _mapStateUrlSidebarName: { [key: string]: string } = {
        dashboard: 'Dashboard',
        'users/agencies': 'Team',
        'users/agents': 'Venditori',
        'contracts/contracts': 'Gestione Contratti',
        'contracts/customers': 'Gestione Clienti',
        'sales/documents': 'Documenti',
        'sales/pricelists': 'Listini',
        cards: 'Loyalty e Carte',
        communications: 'Comunicazioni',
        statistics: 'Statistiche',
        profile: 'Profilo'
    };

    constructor(public dataService: DataService, public router: Router) {
        super(dataService, router);
    }

    canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const stateUrl = next.routeConfig.path;
        return this.acccessThisRouteOrRedirect(
            this._role,
            stateUrl,
            this._urlPref,
            this._mapStateUrlSidebarName
        );
    }
}

/**AUTH GUARD AGENCY */
@Injectable({ providedIn: 'root' })
export class AuthGuardAgency extends AuthGuardBase implements CanActivateChild {
    _role: PataviumRole = PataviumRole.Agency;
    _urlPref = '/agency/';
    _mapStateUrlSidebarName: { [key: string]: string } = {
        dashboard: 'Dashboard',
        agents: 'Venditori',
        'agency-operators': 'Operatori Team',
        'contracts/contracts': 'Gestione Contratti',
        'contracts/customers': 'Gestione Clienti',
        'sales/documents': 'Documenti',
        'sales/pricelists': 'Listini',
        'communications/operators': 'Comunicazioni Patavium',
        'communications/agents': 'Comunicazioni Venditori',
        statistics: 'Statistiche',
        profile: 'Profilo'
    };

    constructor(public dataService: DataService, public router: Router) {
        super(dataService, router);
    }

    canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const stateUrl = next.routeConfig.path;
        return this.acccessThisRouteOrRedirect(
            this._role,
            stateUrl,
            this._urlPref,
            this._mapStateUrlSidebarName
        );
    }
}

/**AUTH GUARD AGENCY OPERATOR */
@Injectable({ providedIn: 'root' })
export class AuthGuardAgencyOperator extends AuthGuardBase implements CanActivateChild {
    _role: PataviumRole = PataviumRole.AgencyOperator;
    _urlPref = '/agency-operator/';
    _mapStateUrlSidebarName: { [key: string]: string } = {
        dashboard: 'Dashboard',
        agents: 'Venditori',
        'contracts/contracts': 'Gestione Contratti',
        'contracts/customers': 'Gestione Clienti',
        'sales/documents': 'Documenti',
        'sales/pricelists': 'Listini',
        'communications/operators': 'Comunicazioni Patavium',
        'communications/agents': 'Comunicazioni Venditori',
        statistics: 'Statistiche',
        profile: 'Profilo'
    };

    constructor(public dataService: DataService, public router: Router) {
        super(dataService, router);
    }

    canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const stateUrl = next.routeConfig.path;
        return this.acccessThisRouteOrRedirect(
            this._role,
            stateUrl,
            this._urlPref,
            this._mapStateUrlSidebarName
        );
    }
}

/**AUTH GUARD AGENT */
@Injectable({ providedIn: 'root' })
export class AuthGuardAgent extends AuthGuardBase implements CanActivateChild {
    _role: PataviumRole = PataviumRole.Agent;
    _urlPref = '/agent/';
    _mapStateUrlSidebarName: { [key: string]: string } = {
        dashboard: 'Dashboard',
        'contracts/contracts': 'Gestione Contratti',
        'contracts/customers': 'Gestione Clienti',
        'sales/documents': 'Documenti',
        'sales/pricelists': 'Listini',
        communications: 'Comunicazioni',
        statistics: 'Statistiche',
        profile: 'Profilo'
    };

    constructor(public dataService: DataService, public router: Router) {
        super(dataService, router);
    }

    canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const stateUrl = next.routeConfig.path;
        return this.acccessThisRouteOrRedirect(
            this._role,
            stateUrl,
            this._urlPref,
            this._mapStateUrlSidebarName
        );
    }
}

@Injectable({ providedIn: 'root' })
export class AuthGuardSpecializedOperator extends AuthGuardBase implements CanActivateChild {
    _role: PataviumRole = PataviumRole.SpecializedOperator;
    _urlPref = '/specialized-operator/';
    _mapStateUrlSidebarName: { [key: string]: string } = {
        'contracts/contracts': 'Gestione Contratti',
        'profile': 'Profilo'
    };

    constructor(public dataService: DataService, public router: Router) {
        super(dataService, router);
    }

    canActivateChild(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        console.debug(`canActivateChild`, this._role, state, next);
        const stateUrl = next.routeConfig.path;
        return this.acccessThisRouteOrRedirect(
            this._role,
            stateUrl,
            this._urlPref,
            this._mapStateUrlSidebarName
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/services/auth.service.ts